// Browser-side encryption for stored passwords (brief §4).
//
// PBKDF2-SHA256, 600k iterations derives a 256-bit AES-GCM key from the master
// password + a per-vault 16-byte salt. Every secret gets a fresh 12-byte IV and
// is stored as `ivB64:ciphertextB64`. The derived key lives in memory only and is
// dropped after 10 minutes of inactivity. The server never sees the password or
// the key — only ciphertext.

export const ITERATIONS = 600_000
const VERIFIER_PLAINTEXT = 'analytix-access-register'
const IDLE_MS = 10 * 60 * 1000

const enc = new TextEncoder()
const dec = new TextDecoder()

/** Copy any typed array into a fresh ArrayBuffer-backed one (satisfies BufferSource). */
const bytes = (u: Uint8Array): Uint8Array<ArrayBuffer> => {
  const out = new Uint8Array(u.byteLength)
  out.set(u)
  return out
}
const utf8 = (s: string) => bytes(enc.encode(s))

export const b64 = (buf: ArrayBuffer | Uint8Array) =>
  btoa(String.fromCharCode(...new Uint8Array(buf)))
export const ub64 = (s: string): Uint8Array<ArrayBuffer> =>
  Uint8Array.from(atob(s), (c) => c.charCodeAt(0))

async function deriveKey(password: string, salt: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey('raw', utf8(password), 'PBKDF2', false, ['deriveKey'])
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: ITERATIONS, hash: 'SHA-256' },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

export async function seal(key: CryptoKey, text: string): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, utf8(text))
  return `${b64(iv)}:${b64(ct)}`
}

export async function open(key: CryptoKey, blob: string): Promise<string> {
  const [iv, ct] = blob.split(':')
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: ub64(iv) }, key, ub64(ct))
  return dec.decode(pt)
}

export interface VaultMeta {
  exists: boolean
  salt?: string
  iterations?: number
  verifier?: string
}

// --- in-memory key store --------------------------------------------
type Listener = () => void

class VaultStore {
  private key: CryptoKey | null = null
  private idle: ReturnType<typeof setTimeout> | null = null
  private listeners = new Set<Listener>()
  /** set while a reveal/set is waiting for the user to unlock */
  needsUnlock = false
  private pending: { resolve: (k: CryptoKey) => void; reject: (e: unknown) => void } | null = null

  subscribe(fn: Listener) {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }
  private emit() {
    this.listeners.forEach((f) => f())
  }

  get unlocked() {
    return this.key !== null
  }

  touch() {
    if (!this.key) return
    if (this.idle) clearTimeout(this.idle)
    this.idle = setTimeout(() => this.lock('idle'), IDLE_MS)
  }

  lock(reason?: 'idle') {
    this.key = null
    if (this.idle) clearTimeout(this.idle)
    this.idle = null
    if (this.pending) {
      this.pending.reject(new Error('locked'))
      this.pending = null
    }
    this.needsUnlock = false
    this.emit()
    return reason
  }

  /**
   * Derive a key + build the /api/vault payload. Does NOT adopt the key — the
   * caller adopts it only after the server confirms the vault was created.
   */
  async prepareSetup(password: string) {
    const salt = crypto.getRandomValues(new Uint8Array(16))
    const key = await deriveKey(password, salt)
    const verifier = await seal(key, VERIFIER_PLAINTEXT)
    return { key, payload: { salt: b64(salt), iterations: ITERATIONS, verifier } }
  }

  /** Adopt a freshly created / verified key. */
  adopt(key: CryptoKey) {
    this.key = key
    this.touch()
    this.needsUnlock = false
    this.resolvePending(key)
    this.emit()
  }

  /** Unlock with an existing vault's meta. Returns false on a wrong password. */
  async unlock(password: string, meta: VaultMeta): Promise<boolean> {
    if (!meta.salt || !meta.verifier) return false
    const key = await deriveKey(password, ub64(meta.salt))
    try {
      if ((await open(key, meta.verifier)) !== VERIFIER_PLAINTEXT) return false
    } catch {
      return false
    }
    this.adopt(key)
    return true
  }

  getKey() {
    return this.key
  }

  /** Resolve with the key, prompting for unlock if needed. */
  ensureKey(): Promise<CryptoKey> {
    if (this.key) {
      this.touch()
      return Promise.resolve(this.key)
    }
    return new Promise<CryptoKey>((resolve, reject) => {
      this.pending = { resolve, reject }
      this.needsUnlock = true
      this.emit()
    })
  }

  cancelUnlock() {
    if (this.pending) {
      this.pending.reject(new Error('cancelled'))
      this.pending = null
    }
    this.needsUnlock = false
    this.emit()
  }

  private resolvePending(key: CryptoKey) {
    if (this.pending) {
      this.pending.resolve(key)
      this.pending = null
    }
  }
}

export const vault = new VaultStore()

// --- password strength (advisory only) ------------------------------
export function strength(pw: string): number {
  let s = 0
  if (pw.length >= 10) s++
  if (pw.length >= 14) s++
  if (/[^A-Za-z0-9]/.test(pw) && /\d/.test(pw)) s++
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++
  return Math.min(s, 4)
}
